import { Spinner } from "@blueprintjs/core"
import classNames from "classnames"
import { observer } from "mobx-react"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { apiUrls } from "modules/common/services/communication/urls"
import React, { useCallback, useLayoutEffect, useRef } from "react"
import {
  FeedbackAttachment,
  FeedbackMessage,
  FeedbackOutgoing,
  FeedbackStoreType,
} from "../models/feedback-store"
import styles from "./Feedback.module.scss"
import { FeedbackComposer } from "./FeedbackComposer"
import { branchTitle, formatDateTime, formatFileSize, isOwnBranch, shortName } from "./format"

/** Насколько близко к краю ленты считаем, что пользователь «у края» */
const EDGE = 60

export const FeedbackThread = observer(({ store, userId }: FeedbackThreadProps) => {
  const listRef = useRef<HTMLDivElement>(null)
  // расстояние от низа ленты до позиции прокрутки перед подгрузкой старых сообщений
  const olderAnchor = useRef<number | null>(null)
  const stickToBottom = useRef(true)

  const branch = store.activeBranch
  const branchId = store.activeBranchId
  const outgoing = store.activeOutgoing

  useLayoutEffect(() => {
    stickToBottom.current = true
  }, [branchId])

  useLayoutEffect(() => {
    const list = listRef.current

    if (!list) {
      return
    }

    if (olderAnchor.current !== null) {
      // подгрузили историю сверху: оставляем перед глазами то же сообщение
      list.scrollTop = list.scrollHeight - olderAnchor.current
      olderAnchor.current = null
      return
    }

    if (stickToBottom.current) {
      list.scrollTop = list.scrollHeight
    }
  }, [store.messages, outgoing.length])

  const scrollToBottomIfSticky = useCallback(() => {
    const list = listRef.current

    if (list && stickToBottom.current) {
      list.scrollTop = list.scrollHeight
    }
  }, [])

  const onScroll = useCallback(() => {
    const list = listRef.current

    if (!list) {
      return
    }

    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < EDGE

    if (list.scrollTop < EDGE && store.hasMore && !store.olderLoading && !store.messagesLoading) {
      olderAnchor.current = list.scrollHeight - list.scrollTop
      store.loadMessages("older").then(() => {
        olderAnchor.current = null
      })
    }
  }, [store])

  const send = useCallback(
    (text: string, attachments: FeedbackAttachment[]) => {
      stickToBottom.current = true
      store.send(text, attachments)
    },
    [store]
  )

  const attachmentUrl = (attachment: FeedbackAttachment) =>
    branchId ? `${store.baseUrl}${apiUrls.feedback.attachment(branchId, attachment.id)}` : ""

  const renderAttachments = (attachments: FeedbackAttachment[], linked: boolean) =>
    attachments.length > 0 && (
      <div className={styles.attachments}>
        {attachments.map((attachment) => {
          const url = linked ? attachmentUrl(attachment) : ""

          if (url && attachment.kind === "image") {
            return (
              <a key={attachment.id} href={url} target="_blank" rel="noopener noreferrer" className={styles.image}>
                <img src={url} alt={attachment.filename} onLoad={scrollToBottomIfSticky} />
              </a>
            )
          }

          const content = (
            <>
              <GeneralIcon type="general-doc" size={14} />
              <span className={styles.fileName}>{attachment.filename}</span>
              <span className={styles.fileSize}>{formatFileSize(attachment.size)}</span>
            </>
          )

          return url ? (
            <a key={attachment.id} href={url} target="_blank" rel="noopener noreferrer" className={styles.file}>
              {content}
            </a>
          ) : (
            <span key={attachment.id} className={styles.file}>
              {content}
            </span>
          )
        })}
      </div>
    )

  const renderMessage = (message: FeedbackMessage) => {
    const incoming = message.direction === "in"
    const own = incoming && message.author.id === userId

    let status = ""
    if (incoming) {
      status = message.seq <= store.staffReadSeq ? "Прочитано разработчиком" : "Доставлено"
    } else if (message.readByClient && store.isAdmin) {
      // отметку ставит первый прочитавший — часто админ; обычному сотруднику чужое ФИО в его чате ни к чему
      status = `Прочитано: ${shortName(message.readByClient.name)}, ${formatDateTime(message.readByClient.at)}`
    }

    return (
      <div
        key={message.id}
        className={classNames(styles.message, {
          [styles.messageOwn]: incoming,
          [styles.messageStaff]: !incoming,
        })}
      >
        <div className={styles.bubble}>
          <div className={styles.meta}>
            <span className={styles.author}>
              {/* свои сообщения без подписи: и так понятно по стороне ленты */}
              {!own && message.author.name}
              {!incoming && <span className={styles.authorRole}> · разработчик</span>}
              {incoming && !own && message.author.position && (
                <span className={styles.authorRole}> · {message.author.position}</span>
              )}
            </span>
            <span className={styles.date}>{formatDateTime(message.createdAt)}</span>
          </div>

          {store.isAdmin && message.audience === "clientAdmins" && (
            <div className={styles.audience}>Только администраторам</div>
          )}

          {message.text && <div className={styles.text}>{message.text}</div>}
          {renderAttachments(message.attachments || [], true)}
          {status && <div className={styles.status}>{status}</div>}
        </div>
      </div>
    )
  }

  const renderOutgoing = (message: FeedbackOutgoing) => (
    <div key={message.clientMessageId} className={classNames(styles.message, styles.messageOwn, styles.pending)}>
      <div className={styles.bubble}>
        <div className={styles.meta}>
          <span className={styles.date}>{formatDateTime(message.sentAt)}</span>
        </div>

        {message.text && <div className={styles.text}>{message.text}</div>}
        {renderAttachments(message.attachments, false)}

        {message.failed ? (
          <div className={styles.failed}>
            <div className={styles.failedText}>Не отправлено: {message.error}</div>
            <div className={styles.failedButtons}>
              <PlanrButton type="lightblueish" size="small" onClick={() => store.retry(message.clientMessageId)}>
                Повторить
              </PlanrButton>
              <PlanrButton type="graybtn" size="small" onClick={() => store.discard(message.clientMessageId)}>
                Удалить
              </PlanrButton>
            </div>
          </div>
        ) : (
          <div className={styles.status}>Отправляется…</div>
        )}
      </div>
    </div>
  )

  const empty = !store.messagesLoading && !store.messagesError && store.messages.length === 0 && outgoing.length === 0

  return (
    <div className={styles.thread}>
      {store.isAdmin && (
        <div className={styles.threadHeader}>
          <div className={styles.threadTitle}>
            {branchTitle(branch, userId)}
            {branch && branch.kind === "employee" && !isOwnBranch(branch, userId) && branch.subjectPosition && (
              <span className={styles.threadSubtitle}> · {branch.subjectPosition}</span>
            )}
          </div>
          <div className={styles.threadHint}>
            {branch && branch.kind === "admins"
              ? "Ответы разработчика в этом чате видят только администраторы"
              : !branch || isOwnBranch(branch, userId)
              ? "Личные обращения: переписку видите только вы и разработчик"
              : "Переписку видят автор обращения и администраторы"}
          </div>
        </div>
      )}

      <div className={styles.list} ref={listRef} onScroll={onScroll}>
        {store.olderLoading && (
          <div className={styles.older}>
            <Spinner size={16} />
          </div>
        )}

        {store.messagesLoading && (
          <div className={styles.state}>
            <Spinner size={30} />
          </div>
        )}

        {store.messagesError && (
          <div className={styles.state}>
            <div className={styles.stateText}>{store.messagesError}</div>
            <PlanrButton
              type="secondary"
              size="small"
              icon="general-redo"
              onClick={() => store.loadMessages("replace")}
            >
              Повторить
            </PlanrButton>
          </div>
        )}

        {empty && (
          <div className={styles.empty}>
            <GeneralIcon type="general-msg" size={40} />
            <div>
              Задайте вопрос разработчику, сообщите об ошибке или предложите улучшение.
              <br />
              Ответ придёт сюда же.
            </div>
          </div>
        )}

        {!store.messagesLoading && !store.messagesError && store.messages.map(renderMessage)}
        {outgoing.map(renderOutgoing)}
      </div>

      <FeedbackComposer store={store} onSend={send} disabled={!!store.messagesError} key={branchId || "new"} />
    </div>
  )
})

interface FeedbackThreadProps {
  store: FeedbackStoreType
  userId: string
}
